// ─────────────────────────────────────────────────────────────────────────
// Liquidaciones de sueldo (27-ago-2026).
//
// Pantalla del ciclo mensual de remuneraciones, ENCIMA de las fichas de
// Trabajadores: se elige el período, "Generar borradores" calcula la
// liquidación de todas las fichas activas con los parámetros previsionales
// del mes (motor en backend/services/calculoLiquidacion.js), y de ahí cada
// una se revisa → aprueba → paga, con su PDF formato chileno.
// Un borrador se puede regenerar (misma acción) o eliminar; una aprobada o
// pagada es historia laboral y no se toca.
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import backend from "../../backend";
import useTitle from "../../hooks/useTitle";
import useIsMobile from "../../hooks/useIsMobile";
import ConfirmModal from "../../components/design-system/ConfirmModal";
import { formatPrice } from "../../utils";

const fmt = n => `$${formatPrice(Math.round(n || 0))}`;
const num = { fontVariantNumeric: "tabular-nums" };
const S = {
  card:  { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: 14 },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" },
  input: { background: "#161616", border: "1px solid #333", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#ddd", boxSizing: "border-box" },
  seccion: { fontSize: 11, fontWeight: 800, color: "#e30613", textTransform: "uppercase", letterSpacing: ".06em", margin: "12px 0 6px" },
  botonChico: { minHeight: 38, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "0 14px" },
  // Tabla desktop (pulido 27-ago)
  th: {
    background: "#1f1f1f", padding: "8px 12px", textAlign: "left", fontSize: 10,
    fontWeight: 700, color: "#999", borderBottom: "1px solid #2e2e2e",
    textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap",
  },
  td: {
    padding: "8px 12px", color: "#bbb", borderBottom: "1px solid #252525",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
};

const ESTADO = {
  borrador: { label: "Borrador", color: "#f39c12", bg: "#2d2410" },
  aprobada: { label: "Aprobada", color: "#3498db", bg: "#0d2030" },
  pagada:   { label: "Pagada",   color: "#2ecc71", bg: "#0f2a1a" },
};

// Período por defecto: el mes actual "YYYY-MM"
const periodoActual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const periodoLegible = p => {
  const [y, m] = String(p || "").split("-");
  return MESES[Number(m) - 1] ? `${MESES[Number(m) - 1]} de ${y}` : p;
};

const Liquidaciones = () => {
  useTitle("Liquidaciones");
  // Pulido desktop 27-ago: móvil intacto (tarjetas expandibles); desktop = tabla
  // por trabajador con fila expandible para el detalle.
  const isMobile = useIsMobile();

  const [periodo, setPeriodo] = useState(periodoActual());
  const [rows, setRows] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [abierta, setAbierta] = useState(null); // _id expandido
  const [ocupada, setOcupada] = useState(null); // _id con acción en curso
  // 28-ago: filtro por estado (chips + badges cliqueables) y confirmaciones
  // vía ConfirmModal (reemplaza los window.confirm nativos).
  const [filtroEstado, setFiltroEstado] = useState(null); // null | borrador | aprobada | pagada
  const [confirma, setConfirma] = useState(null); // { titulo, mensaje, label, variant, run }

  const avisar = (tipo, texto) => { setAviso({ tipo, texto }); setTimeout(() => setAviso(null), 6000); };

  const cargar = async p => {
    setCargando(true);
    try {
      const r = await backend.get(`liquidacion?periodo=${p}`);
      setRows(r.data || []);
    } catch (e) { console.error(e); }
    setCargando(false);
  };
  useEffect(() => { cargar(periodo); }, [periodo]);

  const generar = () => setConfirma({
    titulo: `Generar borradores — ${periodoLegible(periodo)}`,
    mensaje: "Se calcula la liquidación de todas las fichas activas. Los borradores existentes del período se recalculan; las aprobadas/pagadas no se tocan.",
    label: "Generar borradores",
    variant: "primary",
    run: doGenerar,
  });

  const doGenerar = async () => {
    setGenerando(true);
    try {
      const r = await backend.post("liquidacion/generar", { periodo });
      const { generadas = 0, regeneradas = 0, saltadas = 0, advertencias = [] } = r.data || {};
      avisar("ok", `${generadas} generadas · ${regeneradas} recalculadas · ${saltadas} saltadas${advertencias.length ? ` · ${advertencias.length} advertencias (ver detalle)` : ""}`);
      cargar(periodo);
    } catch (e) {
      avisar("error", e?.response?.data?.error || e.message);
    }
    setGenerando(false);
  };

  const accion = async (liq, verbo) => {
    setOcupada(liq._id);
    try {
      if (verbo === "eliminar") {
        await backend.delete(`liquidacion/${liq._id}`);
        avisar("ok", "Borrador eliminado");
      } else {
        await backend.patch(`liquidacion/${liq._id}/${verbo}`);
        avisar("ok", verbo === "aprobar" ? "Liquidación aprobada" : "Liquidación marcada como pagada");
      }
      cargar(periodo);
    } catch (e) {
      avisar("error", e?.response?.data?.error || e.message);
    }
    setOcupada(null);
  };

  // Abre el ConfirmModal para una acción sobre una liquidación (28-ago)
  const pedirAccion = (liq, verbo) => {
    const f = liq.fichaSnapshot || {};
    const nombre = `${f.nombres || ""} ${f.apellidos || ""}`.trim() || "—";
    const cfg = {
      aprobar:  { titulo: `Aprobar liquidación — ${nombre}`, mensaje: `Líquido a pagar: ${fmt(liq.totales?.liquido)}. Después de aprobar ya no se recalcula.`, label: "Aprobar", variant: "primary" },
      pagar:    { titulo: `Marcar como pagada — ${nombre}`,  mensaje: `Se registra el pago del líquido ${fmt(liq.totales?.liquido)}. Queda como historia laboral.`, label: "Marcar pagada", variant: "success" },
      eliminar: { titulo: `Eliminar borrador — ${nombre}`,   mensaje: "El borrador se elimina. Se puede volver a generar con \"Generar borradores\".", label: "Eliminar borrador", variant: "danger" },
    }[verbo];
    if (!cfg) return;
    setConfirma({ ...cfg, run: () => accion(liq, verbo) });
  };

  const verPdf = async liq => {
    setOcupada(liq._id);
    try {
      const res = await backend.get(`liquidacion/${liq._id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) {
      avisar("error", "No se pudo generar el PDF");
    }
    setOcupada(null);
  };

  const totalLiquidos = rows.reduce((s, l) => s + (l.totales?.liquido || 0), 0);
  const sinVerificar = rows.some(l => l.contexto?.parametrosVerificados === false);

  // Filtro por estado (28-ago): chips con contador + badges cliqueables
  const visibles = filtroEstado ? rows.filter(l => l.estado === filtroEstado) : rows;
  const toggleEstado = k => setFiltroEstado(prev => (prev === k ? null : k));

  const tablaLineas = (titulo, lineas, colorMonto) => (
    <div style={{ flex: 1, minWidth: 240 }}>
      <div style={S.seccion}>{titulo}</div>
      {(lineas || []).length === 0 ? (
        <div style={{ fontSize: 11, color: "#8a8a8a" }}>—</div>
      ) : (
        (lineas || []).map((l, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "3px 0", borderBottom: "1px solid #2a2a2a" }}>
            <div style={{ fontSize: 12, color: "#ddd" }}>
              {l.glosa}
              {l.detalle && <div style={{ fontSize: 10, color: "#8a8a8a" }}>{l.detalle}</div>}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: colorMonto, ...num, whiteSpace: "nowrap" }}>{fmt(l.monto)}</div>
          </div>
        ))
      )}
    </div>
  );

  // Detalle de una liquidación — compartido por la tarjeta móvil expandida y
  // la fila expandible de la tabla desktop.
  const detalleLiq = liq => {
    const trabajando = ocupada === liq._id;
    return (
      <>
        {(liq.advertencias || []).length > 0 && (
          <div style={{ fontSize: 11, color: "#f39c12", margin: "8px 0" }}>
            {liq.advertencias.map((a, i) => <div key={i}>⚠ {a}</div>)}
          </div>
        )}

        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          {tablaLineas("Haberes", liq.haberes, "#2ecc71")}
          {tablaLineas("Descuentos", liq.descuentos, "#e74c3c")}
        </div>

        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginTop: 4 }}>
          {tablaLineas("Aportes del empleador (costo empresa)", liq.aportesEmpleador, "#3498db")}
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={S.seccion}>Totales</div>
            {[
              ["Total imponible", liq.totales?.imponible],
              ["Base tributable", liq.totales?.tributable],
              ["Total haberes", liq.totales?.haberes],
              ["Total descuentos", liq.totales?.descuentos],
              ["Líquido a pagar", liq.totales?.liquido],
              ["Costo empresa", liq.totales?.costoEmpresa],
            ].map(([k, v]) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: "1px solid #2a2a2a" }}>
                <span style={{ fontSize: 12, color: k === "Líquido a pagar" ? "#fff" : "#b8b8b8", fontWeight: k === "Líquido a pagar" ? 800 : 400 }}>{k}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#ddd", ...num }}>{fmt(v)}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          {liq.estado === "borrador" && (
            <button disabled={trabajando} className="t-02"
              onClick={() => pedirAccion(liq, "aprobar")}
              style={{ ...S.botonChico, border: "none", background: "#3498db", color: "#fff" }}>
              Aprobar
            </button>
          )}
          {liq.estado === "aprobada" && (
            <button disabled={trabajando} className="t-02"
              onClick={() => pedirAccion(liq, "pagar")}
              style={{ ...S.botonChico, border: "none", background: "#2ecc71", color: "#08240f" }}>
              Marcar pagada
            </button>
          )}
          <button disabled={trabajando} className="t-02" onClick={() => verPdf(liq)}
            style={{ ...S.botonChico, border: "1px solid #333", background: "#1e1e1e", color: "#ddd" }}>
            {trabajando ? "…" : "Ver PDF"}
          </button>
          {liq.estado === "borrador" && (
            <button disabled={trabajando} className="t-02"
              onClick={() => pedirAccion(liq, "eliminar")}
              style={{ ...S.botonChico, border: "1px solid #4a2020", background: "#2d1515", color: "#e74c3c", marginLeft: "auto" }}>
              Eliminar borrador
            </button>
          )}
        </div>
      </>
    );
  };

  return (
    <div style={{ padding: "12px 12px 24px", background: "#1a1a1a", minHeight: "100%", color: "#ddd", maxWidth: isMobile ? 860 : 1200 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: "#fff" }}>🧾 Liquidaciones de sueldo</div>
        {aviso && (
          <span style={{ fontSize: 12, fontWeight: 700, color: aviso.tipo === "ok" ? "#2ecc71" : "#e74c3c" }}>
            {aviso.tipo === "ok" ? "✓ " : "⚠ "}{aviso.texto}
          </span>
        )}
      </div>

      {/* ── Período + generar ── */}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ ...S.label, marginBottom: 4 }}>Período</div>
          <input type="month" style={{ ...S.input, ...num }} value={periodo} onChange={e => e.target.value && setPeriodo(e.target.value)} />
        </div>
        <button onClick={generar} disabled={generando} className="t-02 tap-card"
          style={{ minHeight: 44, borderRadius: 10, border: "none", background: generando ? "#333" : "#e30613", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", padding: "0 20px" }}>
          {generando ? "Calculando…" : "Generar borradores"}
        </button>
        {rows.length > 0 && (
          <span style={{ fontSize: 12, color: "#8a8a8a", ...num, paddingBottom: 12 }}>
            {rows.length} {rows.length === 1 ? "liquidación" : "liquidaciones"} · líquidos {fmt(totalLiquidos)}
          </span>
        )}
      </div>

      {/* Chips por estado (28-ago): Borrador/Aprobada/Pagada con contador */}
      {rows.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
          {Object.entries(ESTADO).map(([k, e]) => {
            const n = rows.filter(l => l.estado === k).length;
            const activo = filtroEstado === k;
            return (
              <button key={k} onClick={() => toggleEstado(k)} className="chip-touch no-touch-min t-02"
                title={activo ? "Quitar el filtro" : `Filtrar: solo ${e.label.toLowerCase()}s`}
                style={{
                  background: activo ? e.bg : "#1e1e1e",
                  color: activo ? e.color : "#999",
                  border: `1px solid ${activo ? e.color : "#333"}`,
                  borderRadius: 8, padding: "6px 12px", fontSize: 11,
                  fontWeight: activo ? 700 : 400, cursor: "pointer", whiteSpace: "nowrap", ...num,
                }}>
                {e.label} ({n}){activo ? " ✕" : ""}
              </button>
            );
          })}
          {filtroEstado && (
            <span style={{ fontSize: 11, color: "#8a8a8a", ...num }}>
              {visibles.length} de {rows.length}
            </span>
          )}
        </div>
      )}

      {sinVerificar && (
        <div style={{ ...S.card, borderColor: "#4a3a10", marginBottom: 12, fontSize: 12, color: "#f39c12" }}>
          ⚠ Hay liquidaciones calculadas con parámetros previsionales SIN VERIFICAR contra Previred.
          Revise los indicadores del período antes de aprobar.
        </div>
      )}

      {/* ── Lista ── */}
      {cargando ? (
        <div style={{ color: "#8a8a8a", fontSize: 12, padding: 16 }}>Cargando…</div>
      ) : rows.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", color: "#8a8a8a", fontSize: 12 }}>
          Sin liquidaciones para {periodoLegible(periodo)}. Genere los borradores desde las fichas activas de Trabajadores.
        </div>
      ) : visibles.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", color: "#8a8a8a", fontSize: 12 }}>
          Ninguna liquidación en estado "{ESTADO[filtroEstado]?.label || filtroEstado}" para {periodoLegible(periodo)}.
        </div>
      ) : !isMobile ? (
        /* ── Desktop: tabla por trabajador con fila expandible ── */
        <div style={{ border: "1px solid #2e2e2e", borderRadius: 8, overflowX: "auto", background: "#232323" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <th style={S.th}>Trabajador</th>
                <th style={{ ...S.th, textAlign: "right" }}>Imponible</th>
                <th style={{ ...S.th, textAlign: "right" }}>Descuentos</th>
                <th style={{ ...S.th, textAlign: "right" }}>Líquido</th>
                <th style={S.th}>Estado</th>
                <th style={{ ...S.th, textAlign: "right" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map(liq => {
                const f = liq.fichaSnapshot || {};
                const est = ESTADO[liq.estado] || ESTADO.borrador;
                const expandida = abierta === liq._id;
                const trabajando = ocupada === liq._id;
                return (
                  <React.Fragment key={liq._id}>
                    <tr onClick={() => setAbierta(expandida ? null : liq._id)} className="bini-row-hover"
                      style={{ cursor: "pointer", background: expandida ? "#262626" : "#232323" }}>
                      <td style={{ ...S.td, whiteSpace: "normal" }}>
                        <span style={{ color: "#fff", fontWeight: 700 }}>{f.nombres} {f.apellidos}</span>
                        <span style={{ color: "#8a8a8a", fontSize: 11 }}> · {f.cargo || "Sin cargo"} · <span style={num}>{f.rut}</span></span>
                        {(liq.advertencias || []).length > 0 && (
                          <span style={{ color: "#f39c12", fontSize: 11 }}> · ⚠ {liq.advertencias.length}</span>
                        )}
                      </td>
                      <td style={{ ...S.td, textAlign: "right", ...num }}>{fmt(liq.totales?.imponible)}</td>
                      <td style={{ ...S.td, textAlign: "right", color: "#e74c3c", ...num }}>{fmt(liq.totales?.descuentos)}</td>
                      <td style={{ ...S.td, textAlign: "right", color: "#fff", fontWeight: 800, ...num }}>{fmt(liq.totales?.liquido)}</td>
                      <td style={{ ...S.td, overflow: "visible" }}>
                        <span
                          onClick={e => { e.stopPropagation(); toggleEstado(liq.estado); }}
                          title={`Filtrar: solo ${est.label.toLowerCase()}s`}
                          style={{ fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 20, color: est.color, background: est.bg, border: `1px solid ${est.color}33`, whiteSpace: "nowrap", cursor: "pointer" }}>
                          {est.label}
                        </span>
                      </td>
                      <td style={{ ...S.td, textAlign: "right", overflow: "visible" }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          {liq.estado === "borrador" && (
                            <button disabled={trabajando} className="t-02"
                              onClick={() => pedirAccion(liq, "aprobar")}
                              style={{ border: "none", background: "#3498db", color: "#fff", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                              Aprobar
                            </button>
                          )}
                          {liq.estado === "aprobada" && (
                            <button disabled={trabajando} className="t-02"
                              onClick={() => pedirAccion(liq, "pagar")}
                              style={{ border: "none", background: "#2ecc71", color: "#08240f", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                              Pagar
                            </button>
                          )}
                          <button disabled={trabajando} className="t-02" onClick={() => verPdf(liq)}
                            style={{ border: "1px solid #333", background: "#1e1e1e", color: "#ddd", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                            {trabajando ? "…" : "PDF"}
                          </button>
                          <span style={{ color: "#8a8a8a", fontSize: 11, alignSelf: "center", cursor: "pointer" }}
                            onClick={() => setAbierta(expandida ? null : liq._id)}>
                            {expandida ? "▲" : "▼"}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {expandida && (
                      <tr>
                        <td colSpan={6} style={{ padding: "6px 16px 16px", background: "#1f1f1f", borderBottom: "1px solid #2e2e2e" }}>
                          {detalleLiq(liq)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {visibles.map(liq => {
            const f = liq.fichaSnapshot || {};
            const est = ESTADO[liq.estado] || ESTADO.borrador;
            const expandida = abierta === liq._id;
            return (
              <div key={liq._id} style={{ ...S.card, padding: "12px 14px" }}>
                <div onClick={() => setAbierta(expandida ? null : liq._id)} className="tap-card t-02"
                  style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", cursor: "pointer" }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{f.nombres} {f.apellidos}</div>
                    <div style={{ fontSize: 11, color: "#b8b8b8", marginTop: 2 }}>
                      {f.cargo || "Sin cargo"} · <span style={num}>{f.rut}</span>
                      {(liq.advertencias || []).length > 0 && (
                        <span style={{ color: "#f39c12" }}> · ⚠ {liq.advertencias.length} {liq.advertencias.length === 1 ? "advertencia" : "advertencias"}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", ...num }}>{fmt(liq.totales?.liquido)}</div>
                    <span
                      onClick={e => { e.stopPropagation(); toggleEstado(liq.estado); }}
                      title={`Filtrar: solo ${est.label.toLowerCase()}s`}
                      style={{ fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 20, color: est.color, background: est.bg, border: `1px solid ${est.color}33`, cursor: "pointer" }}>
                      {est.label}
                    </span>
                  </div>
                </div>

                {expandida && (
                  <div style={{ marginTop: 10, borderTop: "1px solid #2e2e2e", paddingTop: 4 }}>
                    {detalleLiq(liq)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmación de acciones (28-ago, reemplaza window.confirm) */}
      <ConfirmModal
        open={!!confirma}
        title={confirma?.titulo}
        description={confirma?.mensaje}
        confirmLabel={confirma?.label || "Confirmar"}
        confirmVariant={confirma?.variant || "primary"}
        onConfirm={async () => { const c = confirma; setConfirma(null); await c?.run?.(); }}
        onCancel={() => setConfirma(null)}
      />
    </div>
  );
};

export default Liquidaciones;
